import _ from "lodash";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import styled from "styled-components";
import { getSpeakingScore } from "../../../api/speaking.api";
import { getExamCheckAnswer, getExamCheckAnswerCard, getExamCheckSubjectInfo, postExamCheckAnswerCard } from "../../../api/testCenter.api";
import { createTestComments, getTestComments, getTestScore, updateTestComments } from "../../../api/tutor.api";
import useAxios from "../../../hooks/useAxios";
import useFile from "../../../hooks/useFile";
import useNotification from "../../../hooks/useNotification";
import { generateExcel } from "../../../lib/excel";
import { generateAnswerColumns, generateAnswerRows, readAnswerExcel, setAnswerStyle } from "../../../lib/excel/AnswerModalExcel";
import { getDetailCode, getDetailCodeLabel, getWebUrl } from "../../../lib/util";
import { IGetSpeakingScore } from "../../../models/speaking.model";
import { IGetExamCheckAnswer, IGetExamCheckSubjectInfo } from "../../../models/testCenter.model";
import { IClassTestInfo, IGetTestComments, IGetTestScore, ITestStudent } from "../../../models/tutor.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import Checkbox from "../../Checkbox";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import AnswerModal from "../../modals/AnswerModal";

const Block = styled.div`
  table {
    th, td {
      text-align: center;
    }
  }
`;

interface IProps {
  setTab: (tab: string) => void;
  testRecord: IClassTestInfo|undefined;
  studentRecord: ITestStudent|undefined;
  myCommentOnly?: boolean;
}

const TestScore: FC<IProps> = ({
  setTab,
  testRecord,
  studentRecord,
  myCommentOnly = false,
}) => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const TE0001 = getDetailCode(commonCodes, 'TE0001');

  const { warningNotification, successNotification } = useNotification();
  const { file, upload } = useFile();
  const { data: res, isLoading, error, execute } = useAxios<IGetTestScore>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetTestComments>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetSpeakingScore>();
  const { data: res4, isLoading: isLoading4, error: error4, execute: execute4 } = useAxios<IGetExamCheckAnswer>();
  const { data: res5, isLoading: isLoading5, error: error5, execute: execute5 } = useAxios<IGetExamCheckSubjectInfo>();
  const { arr: submitRes, isLoading: submitIsLoading, error: submitError, executeArr: submitExecute } = useAxios();

  const [comments, setComments] = useState<any>();
  const [showAnswerVisible, setShowAnswerVisible] = useState(false);
  const [examSubjectCode, setExamSubjectCode] = useState<number>();

  useEffect(() => {
    if (studentRecord) {
      doGetTestScore();
      doGetTestComments();
    } else {
      setTab('test');
    }
  }, [studentRecord]);

  useEffect(() => {
    if (studentRecord && testRecord?.examDiv === 'LT') {
      execute3(getSpeakingScore(studentRecord.examTestCode));
    }
  }, [studentRecord, testRecord]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      const _comments = _.cloneDeep(res2?.data);
      _comments.forEach((item: any) => {
        item.comment = item.teacherComment || '';
        item.openYn = item.openYn || 'N';
      });
      setComments(_comments);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (submitRes.length && !submitIsLoading) {
      doGetTestComments();
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    (async () => {
      if (!studentRecord) return;
      if (file) {
        const result = await readAnswerExcel(file);
        for (const { examSubjectCode: esc, body } of result) {
          await postExamCheckAnswerCard(studentRecord.examTestCode, esc, body);
        }
        doGetTestScore();
        successNotification(t('class.class.message33'));
      }
    })();
  }, [file]);

  useEffect(() => {
    async function handleData () {
      if (!res5 || !studentRecord) return;
      try {
        const columns = generateAnswerColumns({
          examTypeName: res?.data.examTypeName,
          examDiv: getDetailCodeLabel(TE0001, res4?.data.examDiv ?? ''),
          examTestCode: studentRecord?.examTestCode,
        });
        let rows: Object[] = [];
        let cells: string[] = [];
        for (let i = 0; i < res5.data.length; i++) {
          const { examSubjectCode, subjectName } = res5.data[i];
          const {
            data: { data },
          } = await getExamCheckAnswerCard(studentRecord.examTestCode, examSubjectCode);
          if (i === 0) {
            cells = cells.concat(
              Array.from(
                { length: data.answerInfo.length },
                (v, i) => `C${i + 4}`,
              ),
            );
          } else {
            const lastIndex = +cells[cells.length - 1].split('C')[1];
            cells = cells.concat(
              Array.from(
                { length: data.answerInfo.length },
                (v, i) => `C${lastIndex + 3 + i}`,
              ),
            );
          }
          rows = rows.concat(
            generateAnswerRows(subjectName, examSubjectCode, data.answerInfo),
          );
        }
        generateExcel('test', 'test', rows, columns, setAnswerStyle, cells);
      } catch (e) {
        warningNotification(t('class.class.message34'));
      }
    }

    if (res4 && !isLoading4 && res5 && !isLoading5) {
      handleData();
    }
  }, [res4, isLoading4, res5, isLoading5]);
  
  useEffect(() => {
    window.addEventListener("message", receiveMessage, false);
    return () => window.removeEventListener("message", receiveMessage);
  }, []);

  const receiveMessage = (event: any) => {
    if (event.data?.message === 'speaking-test-end' && studentRecord) {
      execute3(getSpeakingScore(studentRecord.examTestCode));
    }
  }

  const doGetTestScore = () => {
    if (!studentRecord) return;
    execute(getTestScore(studentRecord.examTestCode));
  }

  const doGetTestComments = () => {
    if (!studentRecord) return;
    execute2(getTestComments(studentRecord.examTestCode));
  }

  const onOpenReportCard = () => {
    if (!studentRecord || !testRecord) {
      return;
    }

    let url = '';
    if (testRecord.eduStepCode === 'MD') {
      url += 'md-';
    }
    if (testRecord.examDiv === 'LT') {
      url += 'level-test';
    } else {
      url += 'monthly-test';
    }

    window.open(`${getWebUrl()}/${url}?examTestCode=${studentRecord.examTestCode}`, '_blank', 'width=1090, height=768, top=100, left=100, fullscreen=no, menubar=no, status=no, toolbar=no, titlebar=no, location=no, scrollbar=yes');
  }

  const onOpenSpeakingTest = () => {
    if (!studentRecord || !res3?.data?.speakingExamCode) return;

    window.open(
      `/cbt/speaking-test/exam?placeTestCode=${studentRecord.examTestCode}&speakingExamCode=${res3.data.speakingExamCode}&memberCode=${studentRecord.memberCode}&memberName=${studentRecord.memKorName}&memberEngName=${studentRecord.memEngName}&gradeCode=${studentRecord.gradeCode}`,
      '_blank',
    ); 
  }

  const onDownloadExcel = () => {
    if (!studentRecord || !testRecord) return;
    execute4(getExamCheckAnswer(testRecord.examCode, studentRecord.examTestCode));
    execute5(getExamCheckSubjectInfo(testRecord.examCode));  
  }

  const onSetCommentOpen = (index: number, checked: boolean) => {
    const newComments = [...comments];
    newComments[index].openYn = checked ? 'Y' : 'N';
    setComments(newComments);
  }

  const onSetComment = (index: number, value: string) => {
    const newComments = [...comments];
    newComments[index].comment = value;
    setComments(newComments);
  }
  
  const onSubmit = () => {
    if (!studentRecord) return;
    const createReq: any = [];
    const updateReq: any = [];
    comments.forEach((item: any) => {
      if (item.commentCode) {
        updateReq.push(item);
      } else {
        createReq.push(item);
      }
    });

    const arr: any = [];
    if (createReq.length) {
      arr.push(createTestComments(studentRecord.examTestCode, createReq));
    }
    if (updateReq.length) {
      arr.push(updateTestComments(studentRecord.examTestCode, updateReq));
    }
    if (arr.length) {
      submitExecute(arr);
    }
  }

  const isCommentDisabled = (memberCode: string) => {
    if (!myCommentOnly) return false;
    
    return memberCode !== auth.memberCode;
  }

  return (
    <>
      {isLoading || isLoading2 || isLoading3 || isLoading4 || isLoading5 || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={error4} />
      <ErrorResponse error={error5} />
      <ErrorResponse error={submitError} />

      <div>
      <div className="comm-bx__between">
        <h3 className="sub-title">{studentRecord?.memKorName} ({studentRecord?.memEngName})</h3>
        {res3?.data
          ? (
              <div className="comm-btn__area right">
                <Button
                  className="btn-basic pink-line"
                  onClick={onOpenSpeakingTest}
                  text={testRecord?.eduStepCode === 'EM' ? 'Speaking' : 'Writing'}
                />
              </div>
            )
          : null}
      </div>

      {
        res?.data
          ? (
            <Block className={((res3?.data)?"row":"")}>
              <div className={((res3?.data)?"col-6":"col-12") + " comm-table__area rowType stats pl-0 pr-0 mt-0"}>
                <table>
                  <colgroup>
                    <col width="33.3%" />
                    <col width="33.3%" />
                    <col width="33.3%" />
                  </colgroup>
                  <tbody>
                    <tr>
                      <th>Subject</th>
                      <th>Score</th>
                      <th>Detail</th>
                    </tr>
                    {res?.data?.subjectList?.map((item, index) => (
                      <tr key={index}>
                        <td>{item.subjectName}</td>
                        <td>{item.score === null ? '-' : Number(item.score)} / {Number(item.totalPoint)}</td>
                        <td>
                          <div className="comm-btn__area justify-content-center">
                            <Button
                              className="btn-basic small mint"
                              onClick={() => {
                                setExamSubjectCode(item.examSubjectCode);
                                setShowAnswerVisible(true);
                              }}
                              text="Detail"
                              disabled={!studentRecord?.examTestCode}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td>Total</td>
                      <td>
                        {res?.data?.studentTotalScore === null ? '-' : Number(res?.data?.studentTotalScore)} / {Number(res?.data?.testTotalScore)}
                      </td>
                      <td>
                        <div className="comm-btn__area justify-content-center">
                          <Button
                            className="btn-basic small mint"
                            onClick={onOpenReportCard}
                            text="Report"
                            disabled={!res?.data?.studentTotalScore}
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
                {
                  res3?.data
                    ? (
                      <div className="col-6">
                        <div className="comm-table__area rowType stats mt-0">
                          <table>
                            <colgroup>
                              <col width="50%" />
                              <col width="50%" />
                            </colgroup>
                            <tbody>
                              <tr>
                                <th>Part</th>
                                <th>Score</th>
                              </tr>      
                              {res3.data.details?.map(item => (
                                <tr>
                                  <td>{item.partName}</td>
                                  <td>{item.score || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )
                    : null
                }
            </Block>
            )
          : null
      }
      <div>
        <div className='comm-table__area rowType stats'>
          <table>
            <tbody>
              <tr>
                <th className="text-center align-middle">{t('class.class.column98')}</th>
                <th className="text-center align-middle">{t('class.class.column28')}</th>
                <th className="text-center align-middle">Comment</th>
              </tr>
              {comments?.map((item: any, index: number) => (
                <tr>
                  <td className="text-center align-middle">
                    <Checkbox
                      checked={item.openYn === 'Y'}
                      onChange={e => onSetCommentOpen(index, e.target.checked)}
                      text=""
                      disabled={myCommentOnly}
                    />
                  </td>
                  <td className="text-center align-middle">
                    {item.teacherKorName}
                  </td>
                  <td>
                    <textarea
                      rows={3}
                      value={item.comment}
                      onChange={e => onSetComment(index, e.target.value)}
                      disabled={isCommentDisabled(item.teacherMemCode)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => setTab('test')}
            text={t('common.button.list')}
          />
          <Button
            className="btn-basic pink-line"
            onClick={upload}
            text="Excel Upload"
          />
          <Button
            className="btn-basic green"
            onClick={onDownloadExcel}
            text="Excel Download"
          />
        </div>
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink"
            onClick={onSubmit}
            text={t('common.button.save')}
          />
        </div>
      </div>

      {showAnswerVisible &&
        testRecord &&
        studentRecord &&
        <AnswerModal
          examCode={testRecord.examCode}
          examTestCode={studentRecord.examTestCode}
          defaultExamSubjectCode={examSubjectCode}
          visible={showAnswerVisible}
          onCancel={() => {
            setShowAnswerVisible(false);
            doGetTestScore();
          }}
        />
      }
      </div>
    </>
  );
}

export default TestScore;
